import { Chip, styled, Tooltip } from '@mui/material';
import { concat, find, transform } from 'lodash';
import moment from 'moment';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CopyText,
  CopyTextId,
  CrudTable,
  CrudTableActionType,
  DataGridColumnDefinition,
  DateLabel,
  FormikSelectOption,
  MailLink,
  OperationTypeLabel,
  StylizedMoney,
  Timer,
  TronscanLink,
} from 'components';
import {
  AppConfigArea,
  AppConfigKey,
  FilterDefinitionType,
  FundsRequestStatus,
  OperationType,
} from 'enums';
import {
  FundsRequestStatusLabel,
  TronSourceLabel,
  EditFundsRequestDialog,
} from 'features/funds-request';
import { CreateUserWalletWrapper } from 'features/wallets';
import {
  useAppConfigValue,
  useQueryFilters,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  FundsRequest,
  FundsRequestFilters,
  PaginatedData,
  Shop,
  User,
} from 'types';
import { filtersUtils, formatUtils, fundsRequestUtils } from 'utils';

import { FundsRequestsActions } from '../FundsRequestsActions';

type Props = {
  archived: boolean;
  queryResult: UseQueryResult<PaginatedData<FundsRequest>>;
  hideTitle?: boolean;
  users?: User[];
  shops?: Shop[];
  additionalColumns?: DataGridColumnDefinition<FundsRequest>[];
  additionalFilters?: FilterDefinition<Partial<FundsRequestFilters>>[];
};

export const AddressChip = styled(Chip)`
  background: none;
`;

export const FundsRequests: React.FC<Props> = ({
  archived,
  queryResult,
  additionalColumns,
  additionalFilters,
  users,
  shops,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.funds_request.funds_requests',
  });

  const { isAdmin, isAdminOrTechOperator, isManager, isMerchant } = useUser();
  const { p2pProviders } = useUserContext();

  const categoriesConfigQueryResult = useAppConfigValue(
    {
      area: AppConfigArea.Platform,
      key: AppConfigKey.InternalTransferCategories,
    },
    { enabled: isAdminOrTechOperator },
  );

  const categoryOptions = useMemo(
    () =>
      transform(
        categoriesConfigQueryResult?.data || {},
        (acc, label, value) => acc.push({ label: label as string, value }),
        [] as FormikSelectOption[],
      ),
    [categoriesConfigQueryResult?.data],
  );

  const getCategoryLabel = useCallback(
    (category: string) =>
      (find(categoryOptions, { value: category })?.label as string) || category,
    [categoryOptions],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<FundsRequest>[] => [
      {
        header: t('fields.date'),
        valueGetter: (item) => (
          <div>
            <CopyTextId id={item.id} />
            <DateLabel>{formatUtils.formatDate(item.createdAt)}</DateLabel>
          </div>
        ),
      },
      {
        header: t('fields.user'),
        hidden: !isManager,
        valueGetter: (item: FundsRequest) =>
          item.user ? (
            <Fragment>
              <div>{item.user.name}</div>
              <div>
                <MailLink email={item.user.email} />
              </div>
              {item.asset?.shop?.name && (
                <div>{formatUtils.formatName(item.asset.shop)}</div>
              )}
              {item.asset?.p2pProvider?.name && (
                <div>{item.asset.p2pProvider.name}</div>
              )}
            </Fragment>
          ) : null,
      },
      {
        header: t('fields.shop'),
        hidden: !isMerchant,
        valueGetter: (item: FundsRequest) =>
          item.asset?.shop?.name && (
            <div>{formatUtils.formatName(item.asset.shop)}</div>
          ),
      },
      {
        header: t('fields.transaction'),
        valueGetter: (item) => (
          <Fragment>
            <div>
              <TronscanLink hash={item.hash} />
            </div>
            {item.recipient &&
              item.operationType === OperationType.Withdrawal && (
                <CreateUserWalletWrapper
                  enabled={isAdmin}
                  data={{
                    userId: item.user?.id!,
                    address: item.recipient,
                    network: item.network,
                  }}
                >
                  <CopyText truncateLength={24} text={item.recipient} />
                </CreateUserWalletWrapper>
              )}
            {item.sender && item.operationType === OperationType.Deposit && (
              <CopyText text={item.sender} truncateLength={24} />
            )}
            <div className="tw-mt-1">
              <TronSourceLabel fundsRequest={item} />
            </div>
          </Fragment>
        ),
      },
      {
        header: t('fields.amount'),
        valueGetter: (item) => (
          <div>
            <StylizedMoney
              variant="body1"
              value={item.amount}
              symbol
              assetCurrencyId={item.asset?.assetCurrencyId}
            />
            <OperationTypeLabel fundsRequest={item} />
          </div>
        ),
      },
      {
        header: t('fields.status'),
        valueGetter: (item: FundsRequest) => (
          <Fragment>
            <div className="tw-inline-block tw-mr-1 tw-mb-1">
              <FundsRequestStatusLabel fundsRequest={item} />
            </div>
            {item.status === FundsRequestStatus.New && item.autoCompleteAt && (
              <Tooltip title={t('timeout_tooltip')}>
                <div className="tw-inline-block">
                  <Timer
                    hideTimeOver
                    hideIcon
                    endTime={moment(item.autoCompleteAt).toDate()}
                  />
                </div>
              </Tooltip>
            )}
          </Fragment>
        ),
      },
      {
        header: t('fields.category'),
        valueClassName: 'tw-min-w-[200px]',
        valueGetter: (item) => (
          <div>
            {item.category && (
              <Chip
                label={getCategoryLabel(item.category)}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
            {item.comment && (
              <div className="tw-text-secondary tw-text-gray-500 tw-italic tw-p-1">
                {item.comment}
              </div>
            )}
          </div>
        ),
        hidden: !isAdminOrTechOperator,
      },
      ...(additionalColumns ? additionalColumns : []),
    ],
    [
      t,
      isManager,
      isMerchant,
      additionalColumns,
      isAdmin,
      isAdminOrTechOperator,
      getCategoryLabel,
    ],
  );

  const { filters } = useQueryFilters<FundsRequestFilters>();

  const filtersDefinitions = useMemo(() => {
    const common = concat(
      filtersUtils.getCommonFilters(
        filters,
        FilterDefinitionType.DateTimeRange,
      ),
      fundsRequestUtils.getCommonFilters(users, shops, p2pProviders),
      {
        label: t('fields.category'),
        name: 'category',
        type: FilterDefinitionType.Select,
        getDisplayName: getCategoryLabel,
        options: categoryOptions,
        hidden: !isAdminOrTechOperator,
      },
    );
    return additionalFilters ? concat(common, additionalFilters) : common;
  }, [
    filters,
    users,
    shops,
    p2pProviders,
    additionalFilters,
    categoryOptions,
    getCategoryLabel,
    isAdminOrTechOperator,
    t,
  ]);

  return (
    <Fragment>
      <FundsRequestsActions
        archived={archived}
        queryResult={queryResult}
        filtersDefinitions={filtersDefinitions}
      />
      <CrudTable
        columns={columns}
        queryResult={queryResult}
        paginated
        actions={
          isAdmin && archived
            ? [
                {
                  type: CrudTableActionType.Details,
                  renderDialog: EditFundsRequestDialog,
                },
              ]
            : undefined
        }
      />
    </Fragment>
  );
};
